import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

/**
 * 价格数据接口
 */
interface PriceData {
  price: string;
  change: string;
  vol24h?: string;
  high24h?: string;
  low24h?: string;
  volume?: string;
  maxPrice?: string;
  minPrice?: string;
  timestamp: string;
}

/**
 * BSC WebSocket上下文接口
 */
interface BscWebSocketContextType {
  status: "connected" | "connecting" | "error";
  error: string | null;
  subscribe: (tokenAddress: string) => void;
  unsubscribe: (tokenAddress: string) => void;
  onPriceUpdate: (callback: (data: any) => void) => () => void;
}

// 创建上下文
const BscWebSocketContext = createContext<BscWebSocketContextType | undefined>(
  undefined
);

// 全局单例WebSocket实例
let globalWsInstance: WebSocket | null = null;
// 全局连接状态
let globalConnectionStatus: "connected" | "connecting" | "error" = "connecting";
// 全局错误信息
let globalConnectionError: string | null = null;
// 全局订阅集合
const globalSubscriptions = new Set<string>();
// 全局回调函数集合
const globalCallbacks = new Set<(data: any) => void>();
// 全局最近消息缓存
const globalLastMessages: {[key: string]: {data: any, timestamp: number}} = {};
// 消息去重的时间窗口（毫秒）
const DEDUPLICATION_WINDOW = 500;
// 重连计时器
let globalReconnectTimeout: NodeJS.Timeout | null = null;
// 重连延迟（毫秒）
const RECONNECT_DELAY = 3000;

// 状态变更监听器集合
const statusChangeListeners = new Set<() => void>();

/**
 * 触发状态变更事件
 */
function triggerStatusChange() {
  statusChangeListeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      console.error("执行状态变更监听器时出错:", error);
    }
  });
}

/**
 * 添加状态变更监听器
 * @param listener 监听器函数
 * @returns 移除监听器的函数
 */
function addStatusChangeListener(listener: () => void): () => void {
  statusChangeListeners.add(listener);
  return () => {
    statusChangeListeners.delete(listener);
  };
}

/**
 * 创建并初始化全局WebSocket连接
 * @param wsUrl WebSocket服务器URL
 * @param setStatus 状态更新函数
 * @param setError 错误更新函数
 */
function initializeGlobalWebSocket(
  wsUrl: string,
  setStatus: React.Dispatch<React.SetStateAction<"connected" | "connecting" | "error">>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  // 如果已经有活跃连接，则不重复创建
  if (globalWsInstance && (globalWsInstance.readyState === WebSocket.CONNECTING || 
                          globalWsInstance.readyState === WebSocket.OPEN)) {
    console.log("已存在活跃的WebSocket连接，跳过初始化");
    return;
  }
  
  // 清理旧的连接
  if (globalWsInstance) {
    try {
      globalWsInstance.onopen = null;
      globalWsInstance.onmessage = null;
      globalWsInstance.onerror = null;
      globalWsInstance.onclose = null;
      globalWsInstance.close();
    } catch (err) {
      console.error("关闭旧WebSocket连接时出错:", err);
    }
    globalWsInstance = null;
  }
  
  // 清理重连计时器
  if (globalReconnectTimeout) {
    clearTimeout(globalReconnectTimeout);
    globalReconnectTimeout = null;
  }
  
  try {
    console.log("正在创建新的WebSocket连接:", wsUrl);
    globalConnectionStatus = "connecting";
    globalConnectionError = null;
    
    // 直接更新组件状态
    setStatus("connecting");
    setError(null);
    
    const ws = new WebSocket(wsUrl);
    globalWsInstance = ws;
    
    ws.onopen = () => {
      console.log("WebSocket连接成功");
      globalConnectionStatus = "connected";
      globalConnectionError = null;
      
      // 直接更新组件状态
      setStatus("connected");
      setError(null);
      
      // 重新订阅之前的代币
      const subscriptions = Array.from(globalSubscriptions);
      if (subscriptions.length > 0) {
        console.log(`重新订阅 ${subscriptions.length} 个代币...`);
        subscriptions.forEach(tokenAddress => sendSubscription(tokenAddress));
      }
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // 处理心跳消息
        if (data.event === "ping") {
          ws.send(JSON.stringify({ event: "pong" }));
          return;
        }
        
        // 消息去重逻辑
        if (data.data) {
          let messageKey = "";
          if (Array.isArray(data.data) && data.data.length > 0) {
            const firstItem = data.data[0];
            messageKey = `${firstItem.tokenAddress || ""}_${firstItem.price || ""}_${firstItem.timestamp || Date.now()}`;
          } else if (typeof data.data === "object") {
            messageKey = `${data.data.tokenAddress || ""}_${data.data.price || ""}_${data.data.timestamp || Date.now()}`;
          }
          
          if (messageKey) {
            const now = Date.now();
            const lastMessage = globalLastMessages[messageKey];
            
            // 检查是否在去重窗口内收到过相同消息
            if (lastMessage && now - lastMessage.timestamp < DEDUPLICATION_WINDOW) {
              return; // 忽略重复消息
            }
            
            // 更新最近消息缓存
            globalLastMessages[messageKey] = {
              data: data.data,
              timestamp: now
            };
            
            // 清理过期的消息缓存
            Object.keys(globalLastMessages).forEach(key => {
              if (now - globalLastMessages[key].timestamp > DEDUPLICATION_WINDOW * 2) {
                delete globalLastMessages[key];
              }
            });
            
            // 通知所有回调
            const callbacks = Array.from(globalCallbacks);
            if (callbacks.length > 0) {
              callbacks.forEach(callback => {
                try {
                  callback(data.data);
                } catch (error) {
                  console.error("执行价格更新回调时出错:", error);
                }
              });
            }
          }
        }
      } catch (error) {
        console.error("解析WebSocket消息时出错:", error);
      }
    };
    
    ws.onerror = () => {
      globalConnectionStatus = "error";
      globalConnectionError = "连接错误";
      
      // 直接更新组件状态
      setStatus("error");
      setError("连接错误");
      
      scheduleReconnect(wsUrl, setStatus, setError);
    };
    
    ws.onclose = () => {
      globalConnectionStatus = "error";
      globalConnectionError = "连接关闭";
      
      // 直接更新组件状态
      setStatus("error");
      setError("连接关闭");
      
      scheduleReconnect(wsUrl, setStatus, setError);
    };
  } catch (error) {
    console.error("创建WebSocket连接时出错:", error);
    globalConnectionStatus = "error";
    globalConnectionError = `连接失败: ${error}`;
    
    // 直接更新组件状态
    setStatus("error");
    setError(`连接失败: ${error}`);
    
    scheduleReconnect(wsUrl, setStatus, setError);
  }
}

/**
 * 安排重新连接
 * @param wsUrl WebSocket服务器URL
 * @param setStatus 状态更新函数
 * @param setError 错误更新函数
 */
function scheduleReconnect(
  wsUrl: string,
  setStatus: React.Dispatch<React.SetStateAction<"connected" | "connecting" | "error">>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (globalReconnectTimeout) {
    clearTimeout(globalReconnectTimeout);
  }
  
  globalReconnectTimeout = setTimeout(() => {
    initializeGlobalWebSocket(wsUrl, setStatus, setError);
  }, RECONNECT_DELAY);
}

/**
 * 发送订阅消息
 * @param tokenAddress 代币地址
 */
function sendSubscription(tokenAddress: string) {
  const normalizedAddress = tokenAddress.toLowerCase();
  
  if (globalSubscriptions.has(normalizedAddress)) {
    return;
  }
  
  if (globalWsInstance?.readyState === WebSocket.OPEN) {
    const message = {
      op: "subscribe",
      args: [
        {
          "channel": "dex-market-v3",
          "chainId": 56,
          "tokenAddress": normalizedAddress
        }
      ]
    };
    globalWsInstance.send(JSON.stringify(message));
    globalSubscriptions.add(normalizedAddress);
  }
}

/**
 * 发送取消订阅消息
 * @param tokenAddress 代币地址
 */
function sendUnsubscription(tokenAddress: string) {
  const normalizedAddress = tokenAddress.toLowerCase();
  
  if (globalWsInstance?.readyState === WebSocket.OPEN) {
    const message = {
      op: "unsubscribe",
      args: [
        {
          channel: "dex-market-v3",
          chainId: 56,
          tokenAddress: normalizedAddress
        }
      ]
    };
    globalWsInstance.send(JSON.stringify(message));
    globalSubscriptions.delete(normalizedAddress);
  }
}

/**
 * BSC WebSocket提供者属性
 */
interface BscWebSocketProviderProps {
  children: React.ReactNode;
  wsUrl: string;
}

export function BscWebSocketProvider({
  children,
  wsUrl,
}: BscWebSocketProviderProps) {
  const [status, setStatus] = useState<"connected" | "connecting" | "error">(
    globalConnectionStatus
  );
  const [error, setError] = useState<string | null>(globalConnectionError);
  
  // 组件卸载标志
  const unmountedRef = useRef(false);
  
  // 取消订阅
  const unsubscribe = useCallback((tokenAddress: string) => {
    sendUnsubscription(tokenAddress);
  }, []);

  // 订阅
  const subscribe = useCallback((tokenAddress: string) => {
    sendSubscription(tokenAddress);
  }, []);

  // 设置价格更新回调
  const onPriceUpdate = useCallback((callback: (data: any) => void) => {
    globalCallbacks.add(callback);
    return () => {
      globalCallbacks.delete(callback);
    };
  }, []);

  // 初始化WebSocket连接
  useEffect(() => {
    // 初始化全局WebSocket连接，传入状态更新函数
    initializeGlobalWebSocket(wsUrl, setStatus, setError);
    
    // 清理函数
    return () => {
      unmountedRef.current = true;
    };
  }, [wsUrl]);

  // 提供上下文值
  const contextValue: BscWebSocketContextType = {
    status,
    error,
    subscribe,
    unsubscribe,
    onPriceUpdate,
  };

  return (
    <BscWebSocketContext.Provider value={contextValue}>
      {children}
    </BscWebSocketContext.Provider>
  );
}

/**
 * 使用BSC WebSocket上下文的自定义钩子
 * @returns BSC WebSocket上下文
 */
export function useBscWebSocket() {
  const context = useContext(BscWebSocketContext);
  if (context === undefined) {
    throw new Error("useBscWebSocket必须在BscWebSocketProvider内部使用");
  }
  return context;
}
